"use client";

import { type Refs, useObjectRef, useRefs } from "@mediamonks/react-kit";
import classNames from "clsx";
import { isString, pascalCase } from "es-toolkit";
import { type ReactElement, useMemo } from "react";
import {
  Clickable,
  type ClickableProps,
} from "../../abstract/Clickable/Clickable";
import { A5Text } from "../../atoms/A5Text/A5Text";
import { createInAnimation } from "./M1Button.animations";
import classes from "./M1Button.module.scss";
import { TypographyVariant } from "../../abstract/Typography/Typography.types.ts";
import { M1ButtonVariant } from "./M1Button.types.ts";
import { A2Icon, A2IconName } from "../../atoms/A2Icon/A2Icon.tsx";
import { useEnabledAnimation } from "../../../hooks/useEnabledAnimation.ts";

export type M1ButtonProps = ClickableProps & {
  variant?: M1ButtonVariant;
  startIcon?: ReactElement | A2IconName;
  endIcon?: ReactElement | A2IconName;
  size?: "small" | "medium" | "large";
};

export type M1ButtonRefs = Refs<{
  self: HTMLButtonElement | HTMLAnchorElement;
  label: HTMLSpanElement;
  startIcon: HTMLSpanElement;
  endIcon: HTMLSpanElement;
}>;

export function M1Button({
  className,
  children,
  variant = "primary",
  size = "medium",
  startIcon,
  endIcon,
  ref,
  ...otherProps
}: M1ButtonProps): ReactElement {
  const refs = useRefs<M1ButtonRefs>({
    self: useObjectRef(ref),
  });

  const hasAnimation = useMemo(
    () => variant === "primary" && size === "large",
    [variant, size],
  );
  useEnabledAnimation(
    () => createInAnimation(refs, hasAnimation),
    [refs, hasAnimation],
    hasAnimation,
    refs.self,
  );

  // Set the label style based on the `variant` property
  const labelStyle = useMemo<TypographyVariant>(
    () =>
      variant === "primary" && size === "large"
        ? "button-l"
        : variant === "link" && size === "large"
          ? "heading-2"
          : "button-m",
    [variant, size],
  );

  const startIconComponent = isString(startIcon) ? (
    <A2Icon name={startIcon} className={classes.icon} ref={refs.startIcon} />
  ) : (
    startIcon
  );

  const endIconComponent = isString(endIcon) ? (
    <A2Icon name={endIcon} className={classes.icon} ref={refs.endIcon} />
  ) : (
    endIcon
  );

  return (
    <Clickable
      className={classNames(
        classes.m1Button,
        className,
        classes[`variant${pascalCase(variant)}`],
        classes[`size${pascalCase(size)}`],
      )}
      // @ts-expect-error refs do not overlap
      ref={refs.self}
      {...otherProps}
    >
      {startIconComponent}
      {children && (
        <A5Text
          variant={labelStyle}
          as="span"
          className={classes.label}
          ref={refs.label}
        >
          {children}
        </A5Text>
      )}
      {endIconComponent}
    </Clickable>
  );
}
